import StartupManager from '../containers/main/StartupManager'
import _ from 'lodash'
import {
  ALLOWED_THEME_PREF,
  MSP_MICRO_APP_NAME,
  MSP_PATH_PREFIX,
  MULTI_TENANT_PATH_PREFIX_REGEX,
  NON_FAWKES_PATH_PREFIX_REGEX,
  SASE_URL_KEY,
  NETSEC_URL_KEY,
  SDWAN_APP_ID,
  SCM_CLIENT_ID,
  SASE_CLIENT_ID,
  MSP_URL_KEY,
  TSG_ID_NONE,
  CN_REGION,
  SASE_CN_URL_KEY,
} from './constants'
import {
  getTsgToken,
  jwtDecodeCached,
  tsgUserManagers,
  setCurrentTsgId,
  fetchTsgInstances,
  fetchTsgs,
  fetchAppManifests,
  indexTsgTree,
  getUserFromManager,
  indexTsgInstances,
} from './tsg'
import { parseAccess, fetchIamCustomRoleDefinition } from './tsg'
import { dispatch, stateRef } from './store'
import { isNonemptyArray, wait, noop } from './utils'
import { showLoadingMask } from './loadingMask'
import { setPageTitle } from './title'
import {
  history,
  events,
  getFrameworkVars,
  getFrameworkServiceUrl,
  isOidcClient,
  getAppCanBeLaunched,
  hasMicroApp,
  getFrameworkVar,
} from './states'
import { __error, __isLocalhost } from '../init/debug'
import type {
  AuthState,
  MainState,
  StoreState,
  AppManifests,
  AccessRoles,
  IamJwtPayload,
  IamRolePermissionDefinition,
  ThemePreferenceValue,
  FavoritePreference,
} from './stateTypes'
import { overrideTsgInstances } from './overrideInstances'
import { getDatadogRum } from './externals'
import cache from './cache'
import preference, { COMMON_THEME, COMMON_FAVORITES, COMMON_LAST_VISITED_TSG_ID } from './preference'
import type { Tsg, TsgUser, ReadonlyTsgInstances, ReadonlyTsgTree, TsgUserManager, TsgIdAwareType } from './tsg'
import type { Instance } from './indexedArrays'
import type { TsgUnloadResult } from './newMicroAppTypes'

const PROTECTED_STATE = ['auth', 'theme']
/**
 * Set values to the top level of global state using object shallow merge
 * @param values - partial of the global state needs to be merged
 */
export const setValues = (values: Partial<StoreState>) => {
  for (const key of PROTECTED_STATE) { // prevent setting protected state
    if (values[key] !== undefined) {
      __error(`state '${key}' is protected, please use other actions to update it`, values)
      delete values[key]
    }
  }
  dispatch({ type: 'set-values', values })
}
export const setValuesInternal = (values: Partial<StoreState>) => dispatch({ type: 'set-values', values })
/**
 * Set values to the global main state using object shallow merge
 * @param values - partial of the main state needs to be merged
 */
export const setMainValues = (values: Partial<MainState>) => dispatch({ type: 'set-main-values', values })
/**
 * Set values to the global auth state using object shallow merge
 * @param values - partial of the auth state needs to be merged
 */
export const setAuthValues = (values: Partial<AuthState> = {}) => {
  if (isNonemptyArray(values.tsgs) && !Object.isFrozen(values.tsgs)) {
    const tsgs = indexTsgTree(values.tsgs as Tsg[])
    values = {
      ...values,
      tsgs,
    }
    const tsg_id = values.tsg_id ?? stateRef.current.auth?.tsg_id
    if (tsg_id) {
      const tsg_name = tsgs.get(tsg_id)?.display_name
      if (tsg_name !== values.tsg_name) {
        values.tsg_name = tsg_name
      }
    }
  }
  else if (values.tsgs && !isNonemptyArray(values.tsgs)) {
    delete values.tsgs
  }
  if (values.instances && !Object.isFrozen(values.instances)) {
    delete values.instances
  }
  dispatch({ type: 'set-auth-values', values })
}

export const hideSideMenu = () => setValues({
  isSideNavCollapsed: false,
  _showSideMenu: false,
})

const fnCache = new Map()
export const updateAccessTokenFn = (tsgId: string) => {
  const key = `updacc-${tsgId}`
  const cachedFn = fnCache.get(key)
  if (cachedFn) {
    return cachedFn
  }
  const fn = (tsgUser: TsgUser) => {
    const { tsg_id, access_token } = stateRef.current.auth || {}
    if (tsgId !== tsg_id || tsgUser.access_token === access_token || tsgUser.expired) {
      return // skip
    }
    const parsed = jwtDecodeCached(tsgUser.access_token, tsgId)
    if (parsed.tsg_id !== tsgId) {
      return // skip, should be blocked actually
    }
    setAuthValues({ // update access token
      access_token: tsgUser.access_token,
      access_token_decoded: parsed,
    })
  }
  fnCache.set(key, fn)
  return fn
}

export const retrySilentSignInWhenFailToRenewFn = (tsgUserManager: TsgUserManager) => {
  const key = `retryrenew-${tsgUserManager.tsg_id}`
  const cachedFn = fnCache.get(key)
  if (cachedFn) {
    return cachedFn
  }
  const fn = (error?: Error) => {
    console.warn('failed to renew token', error)
    getUserFromManager(tsgUserManager, { forceSignIn: true }).catch((err) => {
      console.warn('failed to fix renew issue', err)
    })
  }
  fnCache.set(key, fn)
  return fn
}

/**
 * Load instances for a given TSG ID, if not given, it will use current TSG, and throw error if no TSG ID is available
 * @param tsg_id - the TSG ID, current TSG ID by default
 * @returns indexed tsg instances array
 */
export const loadTsgInstances = (tsg_id: string | undefined = stateRef.current.auth?.tsg_id): Promise<ReadonlyTsgInstances> => {
  if (!tsg_id) {
    return Promise.reject(new Error('tsg_id is required'))
  }
  const isEq = (curInstances: ReadonlyTsgInstances | undefined, nextInstances: ReadonlyTsgInstances) => {
    if (!curInstances || curInstances.length !== nextInstances.length ||
      curInstances.tsg_id !== nextInstances.tsg_id) {
      return false
    }
    return curInstances.every((inst: Instance) => {
      const nextInst = nextInstances.get(inst.tenant_id)
      return nextInst && nextInst.status === inst.status && nextInst.url === inst.url
    })
  }
  return fetchTsgInstances(tsg_id).then((instances) => {
    const currentTsg = stateRef.current.auth?.tsg_id
    if (currentTsg && instances.tsg_id === currentTsg &&
      !isEq(stateRef.current.auth?.instances, instances)) {
      setAuthValues({ instances })
    }
    return instances
  })
}

export const loadTsgInstancesUsingCache = (tsgId: string, callback: (instances: ReadonlyTsgInstances | undefined) => void): void => {
  if (!tsgId) {
    return
  }
  const instances = stateRef.current.auth?.instances
  const defaultInstances = instances?.tsg_id === tsgId && instances.length ? instances : undefined
  if (defaultInstances) {
    callback(defaultInstances)
  }
  let savedInstances: ReadonlyTsgInstances | undefined
  const cacheKey = `sparky:tsg-instances:${tsgId}`
  const loadPromise = loadTsgInstances(tsgId).then((instances) => {
    _.sortBy(instances, ['app_id', 'tenant_id'])
    cache.set(cacheKey, instances, { ttl: 3600 }).catch(noop)
    if (savedInstances) {
      if (_.isEqual(savedInstances, instances)) { // no change
        return savedInstances // use cache to avoid re-render
      }
      callback(instances)
    }
    savedInstances = instances
    return instances
  })
  const cachePromise = cache.get(cacheKey).then((cached: Instance[]) => {
    if (!cached?.length) {
      if (!savedInstances) {
        callback(defaultInstances)
      }
      return loadPromise
    }
    if (!savedInstances) {
      savedInstances = indexTsgInstances(overrideTsgInstances(cached, tsgId), tsgId)
    }
    return savedInstances
  }, () => {
    if (!savedInstances) {
      callback(defaultInstances)
    }
    return loadPromise
  })
  Promise.race([cachePromise, loadPromise]).then(callback)
}

/**
 * Load the TSG tree structure, any user accessible TSG ID can be used, and use current TSG if not given, but throw error if no TSG ID is available
 * @param tsg_id - the TSG ID, current TSG ID by default
 * @returns indexed tsg tree
 */
export const loadTsgTree = async (tsg_id?: string): Promise<ReadonlyTsgTree> => {
  const jwt = await getTsgToken(tsg_id || stateRef.current.auth?.tsg_id).catch(() =>
    stateRef.current.oidc?.oidcUser?.access_token // try initial token
  )
  return fetchTsgs(jwt).then((tsgs) => {
    const currentTsgs = stateRef.current.auth?.tsgs
    if (currentTsgs !== tsgs && !tsgs.error) {
      setAuthValues({ tsgs })
    }
    return tsgs
  })
}

/**
 * Load and cache the app manifests, any user accessible TSG ID can be used, and use current TSG if not given, but throw error if no TSG ID is available
 * @param tsg_id - the TSG ID, current TSG ID by default
 * @returns cached indexed app manifests
 */
export const loadApps = async (tsg_id?: string): Promise<AppManifests> => {
  if (stateRef.current.auth?.apps?.length) { // cache
    return stateRef.current.auth?.apps
  }
  const jwt = await getTsgToken(tsg_id || stateRef.current.auth?.tsg_id).catch(() =>
    stateRef.current.oidc?.oidcUser?.access_token // try initial token
  )
  const apps = await fetchAppManifests(jwt)
  if (!stateRef.current.auth?.apps?.length) {
    setAuthValues({ apps })
  }
  return apps
}

export const fetchIamCustomRoleDefinitionAndCache = (tsg_id: string) => {
  return fetchIamCustomRoleDefinition(tsg_id).then((customRoles) => {
    if (customRoles?.tsg_id) {
      cache.set(`custom-roles-${tsg_id}`, customRoles, { ttl: 3600 }).catch((err) => {
        __error('failed to cache custom roles for tsg', tsg_id, err)
      }) // no wait
    }
    return customRoles
  })
}

/**
 * Load the per tsg custom role definition, any user accessible TSG ID can be used, and use current TSG if not given, but throw error if no TSG ID is available
 * @param tsg_id - the TSG ID, current TSG ID by default
 * @returns indexed custom role definition
 */
export const loadCustomRoleDefinition = (tsg_id?: string) => { // always refresh cache
  const tsgId = tsg_id || stateRef.current.auth?.tsg_id!
  return fetchIamCustomRoleDefinitionAndCache(tsgId).then((customRoles) => {
    const currentCustomRoles = stateRef.current.auth?.rolePermissionDefinition?.customRoles
    if (customRoles && currentCustomRoles?.tsg_id === tsgId && !_.isEqual(currentCustomRoles, customRoles)) {
      setAuthValues({
        rolePermissionDefinition: {
          ...stateRef.current.auth?.rolePermissionDefinition!,
          customRoles,
        }
      })
    }
    return customRoles
  })
}

export type RequireReload = (authUpdate: any) => boolean | undefined
export type AuthUpdate = {
  tsg_id: string
  tsg_name?: string
  prev_tsg_id?: string
  next_tsg_id?: string
  access_token?: string
  forceReload?: boolean
  access?: AccessRoles
  instances?: ReadonlyTsgInstances
  rolePermissionDefinition?: IamRolePermissionDefinition
  waitPromise?: Promise<TsgUnloadResult>
}
/**
 * Switch between TSG by providing a TSG ID, it may result a browser reload if Fawkes needs to be loaded
 * @param tsg_id - TSG ID need to switch to
 * @param options.forceReload optional force reload
 * @param options.requireReload optional options used internally
 */
export const switchTsg = async (tsg_id: string, options?: { forceReload?: boolean, requireReload?: RequireReload }): Promise<AuthUpdate> => {
  if (!stateRef.current.auth) {
    throw new Error('tsg is not ready or not supported')
  }
  if (!tsg_id) {
    throw new Error('TSG ID is required')
  }
  const prev_tsg_id = stateRef.current.auth.tsg_id
  if (tsg_id === prev_tsg_id) {
    events.emit('switch-tsg-skipped', { tsg_id, prev_tsg_id })
    const instances = stateRef.current.auth.instances
    return { tsg_id: prev_tsg_id, prev_tsg_id, instances }
  }
  events.emit('switch-tsg-requested', { tsg_id, prev_tsg_id })
  setAuthValues({ next_tsg_id: tsg_id, loading: true })
  if (tsg_id === TSG_ID_NONE || tsg_id === '') { // may not actually work if any tsg remain, just cleanup and force refresh
    setCurrentTsgId(undefined)
    await stateRef.current.odic?.userManager?.removeUser().catch(noop)
    window.location.reload()
    return { tsg_id: TSG_ID_NONE, forceReload: true }
  }
  const access_token = await getTsgToken(tsg_id).catch((err) => {
    const error = new Error(`Failed to switch tenant. ${err.message}`)
    error.cause = err
    events.emit('switch-tsg-failed', { tsg_id, prev_tsg_id, error })
    setAuthValues({ next_tsg_id: undefined, loading: false })
    throw error
  })
  const newMgr = tsgUserManagers[tsg_id]
  if (!newMgr) { // should never happen
    const error = new Error('Failed to switch tenant')
    error.cause = new Error('failed to get tsg user manager')
    events.emit('switch-tsg-failed', { tsg_id, prev_tsg_id, error })
    setAuthValues({ next_tsg_id: undefined, loading: false })
    throw error
  }
  newMgr.events.addUserLoaded(updateAccessTokenFn(tsg_id))
  newMgr.events.addSilentRenewError(retrySilentSignInWhenFailToRenewFn(newMgr))
  const prevMgr = prev_tsg_id && tsgUserManagers[prev_tsg_id]
  if (prevMgr) {
    prevMgr.events.removeUserLoaded(updateAccessTokenFn(prev_tsg_id))
    prevMgr.events.removeSilentRenewError(retrySilentSignInWhenFailToRenewFn(prevMgr))
  }
  const tsg_name = stateRef.current.auth.tsgs?.get(tsg_id)?.display_name
  const authUpdate = {
    tsg_id,
    tsg_name,
    prev_tsg_id,
    access_token,
  } as AuthUpdate
  const unloadParam = { ...authUpdate }
  const emitUnload = async () => {
    events.emit('switch-tsg-unload', unloadParam)
    if (unloadParam.forceReload) {
      authUpdate.forceReload = true
    }
    if (unloadParam.waitPromise) {
      const result = await unloadParam.waitPromise
      if (result?.forceReload) {
        authUpdate.forceReload = true
      }
    }
  }
  if (options?.forceReload || authUpdate.forceReload) {
    await emitUnload()
    setAuthValues({
      ...authUpdate,
      prev_tsg_id: undefined,
      next_tsg_id: undefined,
      loading: false,
    })
    if (prev_tsg_id === TSG_ID_NONE && tsgUserManagers.none) {
      await tsgUserManagers.none.removeUser()
      delete tsgUserManagers.none
    }
    setCurrentTsgId(tsg_id)
    window.location.reload()
    return { ...authUpdate, forceReload: true }
  }
  const decoded = jwtDecodeCached(access_token, tsg_id) as IamJwtPayload
  const tsgInstancesPromise = fetchTsgInstances(tsg_id).catch((err) => err.instances)
  const customRoles = await fetchIamCustomRoleDefinitionAndCache(tsg_id)
  authUpdate.instances = await tsgInstancesPromise
  if (stateRef.current.auth.rolePermissionDefinition?.customRoles?.length || customRoles.length) {
    const {
      roles,
      permissionSets,
    } = stateRef.current.auth.rolePermissionDefinition || {}
    authUpdate.rolePermissionDefinition = { roles, permissionSets, customRoles }
  } // skip update if both old and new has empty custom roles
  authUpdate.access = parseAccess(
    decoded?.access,
    authUpdate.rolePermissionDefinition || stateRef.current.auth.rolePermissionDefinition,
  )
  await emitUnload()
  events.emit('switch-tsg-loaded', authUpdate)
  if (prev_tsg_id === TSG_ID_NONE && tsgUserManagers.none) {
    const initUserManager = tsgUserManagers.none
    await initUserManager.removeUser()
    delete tsgUserManagers.none
    setCurrentTsgId(tsg_id)
    try {
      getDatadogRum()?.setGlobalContextProperty?.('global_tsg_id', tsg_id)
    }
    catch (e) {
      __error('failed to set datadog context global_tsg_id', e)
    }
    await initUserManager.signinSilent().then(async (user) => {
      if (!user?.access_token) {
        throw new Error('No access token returned')
      }
      const decoded = jwtDecodeCached(user.access_token, tsg_id) as TsgIdAwareType
      if (decoded.tsg_id !== tsg_id) {
        await initUserManager.removeUser().catch(noop)
        throw new Error('tsg_id not matched')
      }
      initUserManager.tsg_id = tsg_id
    }).catch((err) => {
      console.error(`force reload due to failed to silent switch from none tsg to tsg ${tsg_id}`, err)
      authUpdate.forceReload = true
      window.location.reload()
    })
  }
  if (authUpdate.waitPromise) {
    const result = await authUpdate.waitPromise
    if (result?.forceReload) {
      authUpdate.forceReload = true
    }
  }
  const requireReload = authUpdate.forceReload || options?.requireReload?.(authUpdate)
  if (!requireReload) {
    setAuthValues({
      ...authUpdate,
      prev_tsg_id: undefined,
      next_tsg_id: undefined,
      loading: false,
    })
    setCurrentTsgId(tsg_id)
    if (getFrameworkVar('use_user_preferences') && preference.get(COMMON_LAST_VISITED_TSG_ID) !== tsg_id) {
      preference.set(COMMON_LAST_VISITED_TSG_ID, tsg_id).catch((err) => {
        console.error('failed to update last visited tsg_id', tsg_id, err)
      })
    }
    events.emit('switch-tsg-completed', authUpdate)
    //
    if (getFrameworkVar('use_favorites')) {
      const favorites = getPreferenceFavorites()?.[tsg_id] || []
      const current = {
        ...stateRef.current.preferences?.tsg?.current,
        tsg_id,
        favorites
      }
      setValuesInternal({
        preferences: {
          ...stateRef.current.preferences,
          tsgs: {
            ...stateRef.current.preferences?.current,
            current
          },
        }
      })
    }
  }
  return authUpdate
}
/**
 * Launch Single or Multi Tenant View, by given a TSG ID, and an optional path.
 * By default it will launch Fawkes if the TSG has it, or launch SD-WAN if TSG has.
 * @param tsgId - TSG ID need to switch to
 * @param path - url path
 */
export const launchFawkes = async (tsgId: string, path?: string) => {
  const vars = getFrameworkVars()
  const { auth = {} as AuthState } = stateRef.current
  const prev_tsg_id = stateRef.current.auth?.tsg_id
  let isOneappReady = auth.instances?.flags?.is_oneapp_ready
  const ONEAPP_OR_SASE = 'oneapp'
  const redirectToShell = (redirectPath: string, baseUrl: string | undefined) => {
    if (baseUrl === ONEAPP_OR_SASE) {
      baseUrl = getFrameworkServiceUrl(isOneappReady ? NETSEC_URL_KEY : SASE_URL_KEY)
      if (redirectPath.startsWith(MSP_PATH_PREFIX)) {
        const mspUrl = getFrameworkServiceUrl(MSP_URL_KEY)
        if (mspUrl) {
          baseUrl = mspUrl
        }
      }
    }
    const url = new URL(redirectPath, baseUrl || window.location.origin)
    url.searchParams.set('tsg_id', tsgId)
    window.location.href = url.toString()
  }
  const redirectOneAppSingleTenant = (redirectPath: string) => {
    if (MULTI_TENANT_PATH_PREFIX_REGEX.test(redirectPath)) {
      return false
    }
    const targetUrl = getFrameworkServiceUrl(isOneappReady ? NETSEC_URL_KEY : SASE_URL_KEY)
    const targetClient = isOneappReady ? getFrameworkVar('netsec_client_id') || SCM_CLIENT_ID : SASE_CLIENT_ID
    if (targetUrl && !isOidcClient(targetClient)) { // client not matched
      redirectToShell(redirectPath, targetUrl)
      return true
    }
    return false
  }
  const redirectToMultiTenant = (redirectPath: string | undefined) => {
    if (!redirectPath?.startsWith(MSP_PATH_PREFIX)) {
      return
    }
    const mspUrl = getFrameworkServiceUrl(MSP_URL_KEY)
    const mspOrigin = mspUrl && new URL(mspUrl).origin
    if (mspOrigin && mspOrigin !== window.location.origin) {
      redirectToShell(redirectPath, mspOrigin)
      return true
    }
  }
  if (!tsgId || tsgId === prev_tsg_id) {
    // skip switch tsg
    hideSideMenu()
    const redirectPath = path || StartupManager.defaultRedirect({ auth, vars, singleInstance: true })
    if (redirectToMultiTenant(redirectPath)) {
      return
    }
    if (auth.instances?.getPlatformRegion() === CN_REGION &&
      getFrameworkServiceUrl(SASE_CN_URL_KEY) && !redirectPath.startsWith(MSP_PATH_PREFIX)) {
      return redirectToShell(redirectPath, getFrameworkServiceUrl(SASE_CN_URL_KEY))
    }
    if (vars.override_pa_for_oneapp && !__isLocalhost && !vars.aiops_free && redirectOneAppSingleTenant(redirectPath)) {
      return
    }
    const notFawkesPath = NON_FAWKES_PATH_PREFIX_REGEX.test(redirectPath)
    if (vars.hub || (redirectPath.startsWith(MSP_PATH_PREFIX) && !hasMicroApp(MSP_MICRO_APP_NAME))) {
      return redirectToShell(redirectPath, ONEAPP_OR_SASE)
    }
    if (!notFawkesPath &&
      vars.fawkes_session && !auth.fawkes_tenant_id &&
      auth?.instances.isFawkesRunning) {
      alert('Prisma Access management is not successfully initialized')
    }
    events.emit('launch-tsg-push', { tsg_id: tsgId, switch_tsg: false, path: redirectPath })
    history.push(redirectPath) // no refresh
    return
  }

  showLoadingMask(true)
  // below are temp behavior, need to have per micro-app report if reload is needed
  const requireReload: RequireReload = vars.switch_tsg_reload === 'always' ? (
    (ign: any) => true
  ) : vars.switch_tsg_reload === 'both' ? ({ instances }) => {
    // require reload if launch none-msp and tsg changed unless both no fawkes and sdwan
    return Boolean(auth.instances?.isFawkesRunning || instances?.isFawkesRunning ||
      auth.instances?.isRunningStatus(SDWAN_APP_ID) || instances?.isRunningStatus(SDWAN_APP_ID))
  } : ({ instances }) => {
    // require reload if launch none-msp and tsg changed unless both no fawkes
    return Boolean(auth.fawkes_tenant_id || instances?.isFawkesRunning)
  }

  const { instances, forceReload } = await switchTsg(tsgId, { requireReload })
  const redirectPath = path || StartupManager.defaultRedirect({
    vars,
    auth: {
      ...auth,
      tsg_id: tsgId,
      instances,
    } as AuthState,
    singleInstance: true,
  })
  isOneappReady = instances?.flags?.is_oneapp_ready
  const eventData = { tsg_id: tsgId, prev_tsg_id, switch_tsg: true, path: redirectPath }
  if (redirectToMultiTenant(redirectPath)) {
    return
  }
  if (forceReload || requireReload({ instances })) {
    setCurrentTsgId(tsgId)
    // ensure clear fawkes tenant id before reload (unsetSession)
    sessionStorage.removeItem('currentTenantId')
    localStorage.removeItem('currentTenantId')
    events.emit('launch-tsg-reload', eventData)
    if (vars.hub || (redirectPath.startsWith(MSP_PATH_PREFIX) && !hasMicroApp(MSP_MICRO_APP_NAME))) {
      return redirectToShell(redirectPath, ONEAPP_OR_SASE)
    }
    if (redirectToMultiTenant(redirectPath)) {
      return
    }
    if (auth.instances?.getPlatformRegion() === CN_REGION &&
      getFrameworkServiceUrl(SASE_CN_URL_KEY) && !redirectPath.startsWith(MSP_PATH_PREFIX)) {
      return redirectToShell(redirectPath, getFrameworkServiceUrl(SASE_CN_URL_KEY))
    }
    if (vars.override_pa_for_oneapp && !__isLocalhost && redirectOneAppSingleTenant(redirectPath)) {
      return
    }
    window.location.href = redirectPath // refresh
  }
  else if (vars.hub || (redirectPath.startsWith(MSP_PATH_PREFIX) && !hasMicroApp(MSP_MICRO_APP_NAME))) {
    return redirectToShell(redirectPath, ONEAPP_OR_SASE)
  }
  else {
    hideSideMenu()
    events.emit('launch-tsg-push', eventData)
    history.push(redirectPath) // no refresh
    await wait(300)
    showLoadingMask(false)
  }
}
/**
 * Same as `launchFawkes`, except the path cannot be multi-tenant view path
 * @param tsgId - TSG ID
 * @param path - curl path, by default fawkes or sdwan
 */
export const launchSingleTenantView = (tsgId: string, path?: string) => {
  if (path && MULTI_TENANT_PATH_PREFIX_REGEX.test(path)) {
    throw new Error('Cannot launch single-tenant view by providing a multi-tenant view path')
  }
  return launchFawkes(tsgId, path)
}
/**
 * Same as `launchFawkes`, except the path cannot be single-tenant view path
 * @param tsgId - TSG ID
 * @param path - curl path, by default fawkes or sdwan
 */
export const launchMultiTenantView = (tsgId: string, path: string | undefined = MSP_PATH_PREFIX) => {
  if (path && MULTI_TENANT_PATH_PREFIX_REGEX.test(path)) {
    return launchFawkes(tsgId, path)
  }
  throw new Error('Cannot launch multi-tenant view by providing a single-tenant view path')
}
/**
 * Similar to `launchSingleTenantView`, instead of given path, it accept an app_id, and go to the default route
 * @param tsgId - TSG ID
 * @param appId - the app_id of the instance, prisma_access, cgx, etc.
 */
export const launchAppDefaultRoute = (tsgId: string, appId: string): Promise<void> => {
  const cfg = getAppCanBeLaunched(appId)
  const default_route = cfg?.default_route
  if (default_route === undefined) {
    return Promise.reject(new Error(`Cannot launch app ${appId} with default route using the current configuration`))
  }
  if (cfg?.force_reload) {
    const { tsgs, tsg_id: prev_tsg_id, access_token } = stateRef.current.auth || {}
    const tsg_name = tsgs?.get(tsgId)?.display_name
    const unloadParam = { tsg_id: tsgId, tsg_name, prev_tsg_id, access_token } as AuthUpdate
    events.emit('switch-tsg-unload', unloadParam)
    const redirect = () => {
      const url = new URL(default_route, window.location.origin)
      url.searchParams.set('tsg_id', tsgId)
      window.location.href = url.toString()
      return Promise.resolve()
    }
    return unloadParam.waitPromise?.then?.(redirect) || redirect()
  }
  return launchSingleTenantView(tsgId, default_route)
}
/**
 * Open the left bottom tenant switcher, send visible as false to close
 * Close tenant switcher will also close common module below
 * @param visible - true by default, false to close
 */
export const openTenantSwitcher = (visible: boolean = true, tsg_id?: string) => {
  visible = Boolean(visible)
  if (getFrameworkVar('new_tenant_app_switcher')) {
    events.emit('open-tenant-switcher', { open: visible, tsg_id })
    return
  }
  const current = stateRef.current.commonModule
  const nextState = {} as any
  if (tsg_id && tsg_id !== current?.tsg?.id && stateRef.current.auth?.tsgs) {
    const tsg = stateRef.current.auth.tsgs.get(tsg_id)
    if (tsg) {
      nextState.tsg = tsg
    }
  }
  if (current?.visible !== visible) {
    Object.assign(nextState, {
      visible,
      path: visible ? '/tenant_switcher' : '',
    })
  }
  if (Object.keys(nextState).length) {
    setValuesInternal({
      commonModule: {
        ...current,
        ...nextState,
      }
    })
  }
}

/**
 * Open the left bottom shared services common module panel by give true or a path, give false to close
 * Close common module will also close tenant switcher above
 * @param pathOrVisible - true by default as tenant switcher, false to close
 */
export const openCommonModule = (pathOrVisible: boolean | string = true, tsg_id?: string) => {
  const current = stateRef.current.commonModule
  if (pathOrVisible === false && !current?.visible) {
    return // skip
  }
  const nextState = {
    ...(pathOrVisible === false ? { // close
      visible: false,
      path: '',
    } : {
      visible: true,
      path: pathOrVisible === true ? '' : (pathOrVisible || ''),
    })
  } as any
  if (tsg_id && tsg_id !== current?.tsg?.id && stateRef.current.auth?.tsgs) {
    const tsg = stateRef.current.auth.tsgs.get(tsg_id)
    if (tsg) {
      nextState.tsg = tsg
    }
  }
  const curVisible = Boolean(current?.visible)
  const curPath = current?.path || ''
  if (!nextState.tsg && nextState.visible === curVisible && nextState.path === curPath) {
    return // skip
  }
  setValuesInternal({
    commonModule: {
      ...current,
      ...nextState,
    }
  })
}

/**
 * Force sign out
 */
export const logout = () => {
  if (!stateRef.current.loggingOut) {
    events.emit('logout')
    dispatch({ type: 'logout' })
  }
}

export const setUserTheme = (theme: ThemePreferenceValue = getFrameworkVars().default_user_theme || 'light') => {
  if (!ALLOWED_THEME_PREF.includes(theme)) {
    throw new Error(`only theme ${ALLOWED_THEME_PREF} are supported`)
  }
  const current = stateRef.current.preferences?.user?.theme
  if (current !== theme) {
    const user = {
      ...stateRef.current.preferences?.user,
      theme,
    }
    setValuesInternal({
      preferences: {
        ...stateRef.current.preferences,
        user,
      }
    })
    localStorage.spk_user_theme = theme

    if (getFrameworkVar('use_user_preferences')) {
      preference.set(COMMON_THEME, theme) // no wait
      sessionStorage.removeItem('spk_user_pref')
    }
    else {
      sessionStorage.spk_user_pref = JSON.stringify(user)
    }
  }
}

export const getPreferenceFavorites = (): Record<string, any> => {
  const fav = preference.get(COMMON_FAVORITES)
  if (!fav || !_.isPlainObject(fav)) {
    return {}
  }
  return fav
}

export const addFavoritedNavItem = (favoriteItem: FavoritePreference) => {
  const currentFavorites = stateRef.current.preferences?.tsgs?.current?.favorites
  const favorites = currentFavorites ? [...currentFavorites] : []
  const existingFavoriteItem = favorites?.find((item: FavoritePreference) => item.path === favoriteItem?.path)
  if (existingFavoriteItem && existingFavoriteItem.name !== favoriteItem.name) {
    // edit
    existingFavoriteItem.name = favoriteItem.name
  }
  else {
    // add new
    favoriteItem.order_index = currentFavorites?.length || 0
    favorites.push(favoriteItem)
  }

  const currTsgId = stateRef.current?.auth?.tsg_id || ''
  const current = {
    ...stateRef.current.preferences?.tsg?.current,
    favorites
  }

  setValuesInternal({
    preferences: {
      ...stateRef.current.preferences,
      tsgs: {
        ...stateRef.current.preferences?.tsgs,
        current
      },
    }
  })

  //
  // Save the changes to backend here
  //
  const currFavorites = getPreferenceFavorites()
  currFavorites[currTsgId] = favorites
  preference.set(COMMON_FAVORITES, currFavorites)
}

export const removeFavoritedNavItem = (path: string) => {

  const currentTSGPreferences = stateRef.current.preferences?.tsgs?.current
  const currTsgId = stateRef.current?.auth?.tsg_id || ''
  const favorites = currentTSGPreferences?.favorites?.filter((el: FavoritePreference) => (el?.path !== path))
  const current = {
    ...stateRef.current.preferences?.tsg?.current,
    favorites
  }

  setValuesInternal({
    preferences: {
      ...stateRef.current.preferences,
      tsgs: {
        ...stateRef.current.preferences?.tsgs,
        current
      },
    }
  })
  //
  // Save the changes to backend here
  //
  const currFavorites = getPreferenceFavorites()
  currFavorites[currTsgId] = favorites
  preference.set(COMMON_FAVORITES, currFavorites)
}

export const RERENDER_EVENT = 'sparky:rerender'
const triggerRerender = () => {
  events.emit(RERENDER_EVENT) // avoid circular import between actions and routes
}
const debouncedRerender = _.debounce(triggerRerender, 200, { leading: false, trailing: true })
export type RerenderActionOption = { immediate?: boolean }
export const forceRerender = ({ immediate }: RerenderActionOption = {}) => {
  if (immediate) {
    triggerRerender()
  }
  else {
    debouncedRerender()
  }
}

export { setPageTitle }

export default { // exposed actions
  setValues, // do not expose setValuesInternal
  setMainValues,
  setAuthValues,
  loadTsgInstances,
  loadTsgInstancesUsingCache,
  loadTsgTree,
  loadCustomRoleDefinition,
  loadApps,
  switchTsg,
  launchFawkes,
  launchSingleTenantView,
  launchMultiTenantView,
  launchAppDefaultRoute,
  openTenantSwitcher,
  openCommonModule,
  forceRerender,
  setUserTheme,
  getPreferenceFavorites,
  addFavoritedNavItem,
  removeFavoritedNavItem,
  setPageTitle,
  logout,
}
